import {
  Select,
  TextBox,
  TextInput,
  Typography,
} from '@telavita-core/react-design-kit'
import React, { ChangeEvent, useEffect } from 'react'

import { Option } from '../../types/option'

import { Controller, useFormContext } from 'react-hook-form'
import { MedicalRecordFormData } from '../../medicalRecordSchema'
import { CustomRadioButton } from '../CustomRadioButton'
import * as S from './styles'
import { OtherOptionInput } from '../OtherOptionInput'

interface SelectReasonOption {
  id: string
  label: string
}

enum TypesReasons {
  CLINICALLY_DISCHARGE = 'CLINICALLY_DISCHARGE',
  LOST_OF_BENEFIT = 'LOST_OF_BENEFIT',
  REFERRAL = 'REFERRAL',
  OTHER = 'OTHER'
}

const reasonOptions: SelectReasonOption[] = [
  { id: TypesReasons.CLINICALLY_DISCHARGE, label: 'Alta por melhora clínica' },
  { id: TypesReasons.LOST_OF_BENEFIT, label: 'Perda do benefício' },
  { id: TypesReasons.REFERRAL, label: 'Encaminhamento' },
  { id: TypesReasons.OTHER, label: 'Outro' },
]

const radioOptions: Option[] = [
  { id: 'patient-needs-to-continue-treatment-yes', name: 'Sim' },
  {
    id: 'patient-needs-to-continue-treatment-no',
    name: 'Não',
  },
]

export function PatientNeedsToContinueTreatment(): JSX.Element {
  const { control, watch, trigger, resetField } = useFormContext<
    MedicalRecordFormData
  >()

  const needToContinueTreatmentValue = watch(
    'patient_needs_to_continue_treatment.need_to_continue_treatment'
  )
  const needToContinueTreatmentReasonValue = watch(
    'patient_needs_to_continue_treatment.reason'
  )

  const needToContinueTreatmentActive =
    needToContinueTreatmentValue === 'patient-needs-to-continue-treatment-no'

  const onlyNumber = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target?.value
    return inputValue?.replace(/\D/g, '')
  }

  useEffect(() => {
    if (needToContinueTreatmentValue) {
      resetField('patient_needs_to_continue_treatment.reason')
      resetField('patient_needs_to_continue_treatment.other_reason')
      resetField('patient_needs_to_continue_treatment.number_of_schedules')
    }
  }, [needToContinueTreatmentValue])

  const hasOtherOption = needToContinueTreatmentReasonValue === TypesReasons.OTHER

  return (
    <S.PatientNeedsToContinueTreatmentContainer>
      <Typography weight='bolder' variant='content2'>
        O paciente tem necessidade clínica de continuar o tratamento de
        psicoterapia? *
      </Typography>
      <S.RadioButtonOptions>
        {radioOptions.map((option) => (
          <Controller
            key={option.id}
            control={control}
            name='patient_needs_to_continue_treatment.need_to_continue_treatment'
            render={({ field, fieldState: { error } }) => (
              <CustomRadioButton
                {...field}
                key={option.id}
                id={option.id as string}
                onChange={() => field.onChange(option.id)}
                label={
                  <Typography variant='content2'>{option.name}</Typography>
                }
                hasError={Boolean(error?.message)}
              />
            )}
          />
        ))}
      </S.RadioButtonOptions>
      {needToContinueTreatmentValue !== undefined && (
      <>
        {!needToContinueTreatmentActive  && !needToContinueTreatmentReasonValue ? (
          <S.InputWrapper>
            <Controller
              control={control}
              name='patient_needs_to_continue_treatment.number_of_schedules'
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  hasError={Boolean(error?.message)}
                  messageError={error?.message}
                  placeholder='Quantidade de consultas *'
                  onInputChange={(value) => field.onChange(onlyNumber(value))}
                  disabled={needToContinueTreatmentActive}
                />
              )}
            />
          </S.InputWrapper>
        ) : (
          <S.InputWrapper>
            <Controller
              control={control}
              name={'patient_needs_to_continue_treatment.reason'}
              render={({ field, fieldState: { error } }) => (
                <>
                  <S.SelectWrapper>
                    <Select
                      {...field}
                      label=''
                      placeholder={'Selecione o motivo *'}
                      onSelect={(value) => field.onChange(value.id)}
                      options={reasonOptions}
                      selected={reasonOptions.find((option) => option.id === field.value)}
                      hasError={Boolean(error?.message)}
                      messageError={error?.message}
                    />
                  </S.SelectWrapper>
                  {hasOtherOption && (
                    <S.InputWrapper>
                      <Controller
                        control={control}
                        name='patient_needs_to_continue_treatment.other_reason'
                        render={({ field, fieldState: { error } }) => (
                          <OtherOptionInput
                            {...field}
                            errorMessage={error?.message}
                            placeholder='Qual?'
                          />
                        )}
                      />
                    </S.InputWrapper>
                  )}
                </>
              )}
            />
          </S.InputWrapper>
        )}
      </>
      )}
    </S.PatientNeedsToContinueTreatmentContainer>
  )
}
